/**
 * Utility functions to create visual representations of resources for PowerPoint export
 */

import { Resource } from '../types';

export interface ResourceVisualization {
  type: 'text' | 'placeholder';
  content: string;
  description: string;
  hasImage?: boolean;
  imageUrl?: string;
}

/**
 * Creates a text description or placeholder for a resource
 */
export function createResourceVisualization(
  resource: Resource,
  resourceId: string
): ResourceVisualization {
  const resourceType = resource.type || 'unknown';
  
  switch (resourceType) {
    case 'image':
      return {
        type: 'placeholder',
        content: resource.url || resource.src || resource.image_url || '',
        description: `Image: ${resource.alt || resource.alt_text || resource.title || resourceId}`,
        hasImage: true,
        imageUrl: resource.url || resource.src || resource.image_url
      };
    
    case 'rich_text':
      return {
        type: 'text',
        content: resource.content || resource.html || resource.text || '',
        description: `Texte formaté: ${resource.title || resource.caption || resourceId}`
      };
    
    case 'number_line':
      return {
        type: 'placeholder',
        content: '',
        description: `Ligne numérique (${resource.min || 0} à ${resource.max || 100})`
      };
    
    case 'ten_frame':
      return {
        type: 'placeholder',
        content: '',
        description: `Cadre de dix (${resource.rows || 1}×${resource.cols || 5})`
      };
    
    case 'count_objects_canvas':
      return {
        type: 'placeholder',
        content: '',
        description: `Comptage d'objets (${resource.objects || 0} objets)`
      };
    
    case 'quiz':
      const questionCount = resource.questions?.length || 0;
      return {
        type: 'placeholder',
        content: '',
        description: `Quiz interactif (${questionCount} questions)`
      };
    
    case 'sorting_canvas':
      return {
        type: 'placeholder',
        content: '',
        description: `Tri et classification (${resource.categories?.length || 0} catégories)`
      };
    
    case 'spatial_canvas':
      return {
        type: 'placeholder',
        content: '',
        description: `Représentation spatiale (grille ${resource.gridSize || '5×5'})`
      };
    
    case 'drawing_board':
      return {
        type: 'placeholder',
        content: '',
        description: `Tableau de dessin (${resource.rows || 10}×${resource.cols || 10})`
      };
    
    case 'timer':
      return {
        type: 'placeholder',
        content: '',
        description: `Minuteur (${resource.seconds || 60} secondes)`
      };
    
    default:
      return {
        type: 'placeholder',
        content: '',
        description: `Ressource ${resourceType}`
      };
  }
}

/**
 * Gets a user-friendly name for a resource type
 */
export function getResourceTypeName(resourceType: string): string {
  const typeNames: Record<string, string> = {
    'image': 'Image',
    'rich_text': 'Texte formaté',
    'number_line': 'Ligne numérique',
    'ten_frame': 'Cadre de dix',
    'count_objects_canvas': 'Comptage d\'objets',
    'quiz': 'Quiz',
    'sorting_canvas': 'Tri et classification',
    'spatial_canvas': 'Représentation spatiale',
    'drawing_board': 'Tableau de dessin',
    'timer': 'Minuteur'
  };
  
  return typeNames[resourceType] || resourceType;
}
